<template>
  <div>
    <income-table
      :data="data"
      :loading="isLoading"
      :search-query="searchQuery"
      @show-details="showOrderDetails($event.order, $event.item)"
    />

    <!-- Order Details Dialog -->
    <guest-order-details-dialog
      v-model="orderDetailsDialog"
      :order="selectedOrder"
      :guest="selectedGuest"
      :changing-status-order-id="changingStatusOrderId"
      @change-status="changeOrderStatus($event.order, $event.guest, $event.newStatus)"
    />
  </div>
</template>

<script>
import IncomeTable from "@/components/guest/IncomeTable.vue";
import reportService from "@/services/report";
import GuestOrderDetailsDialog from "@/components/dialogs/GuestOrderDetailsDialog.vue";
import hasPrinting from "@/mixins/hasPrinting";
import hasExcel from "@/mixins/hasExcel";
import hasSnackbar from "@/mixins/hasSnackbar";

export default {
  name: "IncomeTab",
  mixins: [hasPrinting, hasExcel, hasSnackbar],
  components: {
    IncomeTable,
    GuestOrderDetailsDialog,
  },
  props: {
    searchQuery: String,
    dateRange: Array,
    isActiveTab: Boolean,
  },
  data() {
    return {
      data: [],
      isLoading: false,
      orderDetailsDialog: false,
      selectedOrder: null,
      selectedGuest: null,
      changingStatusOrderId: null,
    };
  },
  computed: {
    headers() {
      return [
        { text: this.$t("r_b"), value: "num", sortable: false },
        { text: this.$t("date"), value: "date", sortable: false },
        {
          text: this.$t("number_of_orders"),
          value: "orderCount",
          sortable: false,
          align: "center",
        },
        { text: this.$t("amount"), value: "amount", sortable: false, align: "right" },
        { text: "", value: "data-table-expand" },
      ];
    },
    restaurantId() {
      return this.$store.state.restaurant?.id;
    },
    totalIncome() {
      return this.data.reduce((sum, item) => sum + item.amount, 0);
    },
    totalOrderCount() {
      return this.data.reduce((sum, item) => sum + item.orders.length, 0);
    },
  },
  watch: {
    isPrinting(isPrinting) {
      if (!isPrinting || !this.isActiveTab || !this.data || this.data.length === 0) {
        return;
      }

      this.printIncomeTable();
    },
    isExcelDownloading(isDownloading) {
      if (!isDownloading || !this.isActiveTab || !this.data || this.data.length === 0) {
        return;
      }

      this.exportIncomeExcel();
    },
    dateRange: {
      handler() {
        this.loadData();
      },
      immediate: true,
    },
  },
  methods: {
    async loadData() {
      if (!this.restaurantId || !this.dateRange || this.dateRange.length !== 2) {
        return;
      }

      this.isLoading = true;
      try {
        const params = {
          date: this.dateRange[0],
          date2: this.dateRange[1],
        };

        const response = await reportService.getCompanyNoContract(
          this.restaurantId,
          params
        );

        this.processData(response.data);
      } catch (error) {
        console.error("Error loading income data:", error);
        this.data = [];
      } finally {
        this.isLoading = false;
      }
    },
    processData(data) {
      const dailyIncome = {};

      // Check if data.orders exists
      const orders = data.orders || data;

      // Iterate through orders grouped by date
      Object.keys(orders).forEach((date) => {
        const usersOrders = orders[date];
        let dayTotal = 0;
        const dayOrders = [];

        // Iterate through each user for this date
        usersOrders.forEach((user) => {
          // Iterate through orders_guest for each user
          if (user.orders_guest && Array.isArray(user.orders_guest)) {
            user.orders_guest.forEach((order) => {
              let orderTotal = 0;

              // Iterate through transactions for each order
              if (
                order.order_guest_transactions &&
                Array.isArray(order.order_guest_transactions)
              ) {
                order.order_guest_transactions.forEach((transaction) => {
                  const price = parseFloat(transaction.price) || 0;
                  const quantity = parseInt(transaction.quantity) || 0;
                  orderTotal += price * quantity;
                });
              }

              dayTotal += orderTotal;
              dayOrders.push({
                id: order.id,
                delivery_time: order.delivery_time,
                state: order.state,
                total: orderTotal,
                transactions: order.order_guest_transactions || [],
                ...order,
                name: user.name,
                lastname: user.lastname,
                phone: user.phone,
              });
            });
          }
        });

        dailyIncome[date] = {
          total: dayTotal,
          orders: dayOrders,
        };
      });

      // Convert to array and sort by date
      this.data = Object.keys(dailyIncome)
        .map((date) => ({
          date: date,
          amount: dailyIncome[date].total,
          orders: dailyIncome[date].orders,
        }))
        .sort((a, b) => new Date(a.date) - new Date(b.date));
    },
    showOrderDetails(order, item) {
      this.selectedOrder = order;
      this.selectedGuest = order; // We don't have guest info in this context
      this.orderDetailsDialog = true;
    },
    async changeOrderStatus(order, guest, newStatus) {
      if (order.state === newStatus) return;

      this.changingStatusOrderId = order.id;
      this.isLoading = true;

      const payload = {
        ...order,
        order_id: order.id,
        user_id: guest.id,
        status: newStatus,
      };

      try {
        await reportService.changeOrderStatus(payload);
        // Update local state
        order.state = newStatus;
        this.selectedOrder = { ...order };
        this.showSuccessSnackbar(this.$t("status_changed_successfully"));
      } catch (error) {
        console.error("Error changing order status:", error);
        this.showErrorSnackbar(this.$t("status_change_error"));
      } finally {
        this.changingStatusOrderId = null;
        this.isLoading = false;
      }
    },
    printIncomeTable() {
      const tableItems = this.data.map((item, index) => ({
        num: index + 1,
        date: item.date,
        orderCount: item.orders.length,
        amount: `${item.amount.toFixed(2)} ${this.restaurantCurrency}`,
      }));

      // Add total row
      tableItems.push({
        num: "",
        date: `<strong>${this.$t("total").toUpperCase()}</strong>`,
        orderCount: `<strong>${this.totalOrderCount}</strong>`,
        amount: `<strong>${this.totalIncome.toFixed(2)} ${
          this.restaurantCurrency
        }</strong>`,
      });

      this.printDocument({
        pageTitle: this.$t("income"),
        title: this.$t("income"),
        subtitle: `${this.localeDate(this.dateRange[0])} - ${this.localeDate(
          this.dateRange[1]
        )}`,
        tables: [
          {
            headers: this.headers
              .filter((h) => h.value !== "data-table-expand")
              .map((h) => ({ text: h.text, value: h.value, align: h.align })),
            items: tableItems,
          },
        ],
      });
    },
    exportIncomeExcel() {
      const tableItems = this.data.map((item, index) => ({
        num: index + 1,
        date: item.date,
        orderCount: item.orders.length,
        amount: item.amount.toFixed(2),
      }));

      // Calculate total order count
      const totalOrderCount = this.data.reduce(
        (sum, item) => sum + item.orders.length,
        0
      );

      // Add total row
      tableItems.push({
        num: "",
        date: this.$t("total").toUpperCase(),
        orderCount: totalOrderCount,
        amount: `${this.totalIncome.toFixed(2)} ${this.restaurantCurrency}`,
      });

      this.downloadExcel({
        fileName: `prihod_${this.dateRange[0]}_${this.dateRange[1]}.xlsx`,
        sheets: [
          {
            name: this.$t("income"),
            title: this.$t("income"),
            subtitle: `${this.localeDate(this.dateRange[0])} - ${this.localeDate(
              this.dateRange[1]
            )}`,
            headers: this.headers
              .filter((h) => h.value !== "data-table-expand")
              .map((h) => ({ text: h.text, value: h.value, align: h.align })),
            items: tableItems,
          },
        ],
      });
    },
  },
};
</script>
